import { useEffect, useMemo, useRef, useState } from 'react'
import { PhoneCall, RefreshCw } from 'lucide-react'
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getPageHeader } from '@/constants'
import { CallsListSkeleton } from '@/components/layout/calls-list-row-skeleton'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import {
  useCallRecords,
  useLiveCallRecordsStream,
  type CallRecordListItem,
} from '@/hooks/use-call-records'
import { LiveCallCard } from '@/components/calls/live-call-card'
import { ConnectionStatusBadge } from '@/components/calls/connection-status-badge'
import { getPluginSlots } from '@/plugin/slots'
import { sortActiveCalls, sortCompletedCalls } from '@/components/calls/calls-board-sort'

const COMPLETION_DWELL_MS = 2800

type LifeFilter = 'all' | 'active' | 'complete'

function completionDwellFromDeadline(
  deadline: number,
  now: number
): { secondsLeft: number; progress: number } {
  const left = Math.max(0, deadline - now)
  const secondsLeft = Math.max(0, Math.ceil(left / 1000))
  const progress = COMPLETION_DWELL_MS > 0 ? (left / COMPLETION_DWELL_MS) * 100 : 0
  return { secondsLeft, progress }
}

/**
 * Position-only layout (outer shell): tween — no spring overshoot, so siblings in the
 * grid move together predictably instead of ringing or snapping at different rates.
 */
const layoutTransitionCalm = {
  type: 'tween' as const,
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
}
const layoutTransitionReducedMotion = { type: 'tween' as const, duration: 0.12, ease: 'linear' as const }

function CardMotionShell({
  row,
  completionDwell,
}: {
  row: CallRecordListItem
  completionDwell?: ReturnType<typeof completionDwellFromDeadline>
}) {
  const reduceMotion = useReducedMotion()
  const posTransition = reduceMotion
    ? layoutTransitionReducedMotion
    : layoutTransitionCalm
  return (
    <motion.div
      layout="position"
      layoutId={row.id}
      transition={posTransition}
      initial={false}
      className="min-w-0"
    >
      <div className="min-w-0">
        <LiveCallCard row={row} completionDwell={completionDwell} />
      </div>
    </motion.div>
  )
}

export function CallsPage() {
  const BoardActions = getPluginSlots().calls.boardActions
  const { title, description } = getPageHeader(
    '/calls/records',
    'Live calls',
    'Real-time board of call records for this deployment.'
  )

  const [lifeFilter, setLifeFilter] = useState<LifeFilter>('all')
  const [pendingCompletionDeadline, setPendingCompletionDeadline] = useState<
    Map<string, number>
  >(() => new Map())
  const [, setDwellTick] = useState(0)

  const prevCompleteRef = useRef<Map<string, boolean>>(new Map())

  const { data, isLoading, error, refetch } = useCallRecords(50)
  const sse = useLiveCallRecordsStream()

  useEffect(() => {
    if (!data?.length) return
    const prev = prevCompleteRef.current
    setPendingCompletionDeadline((p) => {
      let next: Map<string, number> | null = null
      for (const row of data) {
        const was = prev.get(row.id)
        if (was === false && row.is_complete && !p.has(row.id)) {
          if (!next) next = new Map(p)
          next.set(row.id, Date.now() + COMPLETION_DWELL_MS)
        }
        prev.set(row.id, row.is_complete)
      }
      return next ?? p
    })
  }, [data])

  useEffect(() => {
    if (pendingCompletionDeadline.size === 0) return
    const t = window.setInterval(() => {
      const now = Date.now()
      setPendingCompletionDeadline((p) => {
        const next = new Map(p)
        for (const [id, deadline] of next) {
          if (now >= deadline) next.delete(id)
        }
        return next.size === p.size ? p : next
      })
      setDwellTick((x) => x + 1)
    }, 250)
    return () => window.clearInterval(t)
  }, [pendingCompletionDeadline.size])

  const nowMs = Date.now()

  const { activeRows, completeRows, sortedSingleFilterRows } = useMemo(() => {
    const rows = [...(data ?? [])]
    const pending = pendingCompletionDeadline

    if (lifeFilter === 'all') {
      const active = sortActiveCalls(
        rows.filter((r) => !r.is_complete || pending.has(r.id))
      )
      const complete = sortCompletedCalls(
        rows.filter((r) => r.is_complete && !pending.has(r.id))
      )
      return { activeRows: active, completeRows: complete, sortedSingleFilterRows: null }
    }

    if (lifeFilter === 'active') {
      const active = sortActiveCalls(
        rows.filter((r) => !r.is_complete || pending.has(r.id))
      )
      return { activeRows: active, completeRows: [], sortedSingleFilterRows: active }
    }

    const complete = sortCompletedCalls(
      rows.filter((r) => r.is_complete && !pending.has(r.id))
    )
    return { activeRows: [], completeRows: [], sortedSingleFilterRows: complete }
  }, [data, lifeFilter, pendingCompletionDeadline])

  const sections =
    lifeFilter === 'all'
      ? { active: activeRows, complete: completeRows }
      : null

  const activeCount =
    lifeFilter === 'all'
      ? activeRows.length
      : lifeFilter === 'active'
        ? sortedSingleFilterRows?.length ?? 0
        : 0

  const noMatchingFilter =
    !isLoading &&
    !error &&
    data &&
    data.length > 0 &&
    ((lifeFilter === 'active' && (sortedSingleFilterRows?.length ?? 0) === 0) ||
      (lifeFilter === 'complete' && (sortedSingleFilterRows?.length ?? 0) === 0))

  function dwellFor(id: string) {
    const d = pendingCompletionDeadline.get(id)
    if (d == null) return undefined
    return completionDwellFromDeadline(d, nowMs)
  }

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title={title}
        description={description}
        icon={<PhoneCall className="h-5 w-5" />}
      />

      <Card>
        <CardHeader className="flex flex-col gap-4 space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle className="text-xl">Live board</CardTitle>
            <ConnectionStatusBadge
              status={sse.status}
              lastEventAt={sse.lastEventAt}
            />
            {lifeFilter === 'all' && activeCount > 0 ? (
              <span className="text-xs font-medium text-muted-foreground rounded-full border bg-muted/40 px-2 py-0.5">
                {activeCount} active
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1 rounded-md border p-1 bg-muted/30">
              {(
                [
                  ['all', 'All'],
                  ['active', 'Active'],
                  ['complete', 'Complete'],
                ] as const
              ).map(([key, label]) => (
                <Button
                  key={key}
                  type="button"
                  size="segmented"
                  variant={lifeFilter === key ? 'secondary' : 'ghost'}
                  onClick={() => setLifeFilter(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghostMuted"
                  size="iconSm"
                  onClick={() => void refetch()}
                >
                  <RefreshCw className="size-4" />
                  <span className="sr-only">Reload list</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Full reload from server</TooltipContent>
            </Tooltip>
            {BoardActions ? (
              <div className="shrink-0">
                <BoardActions placement="header" />
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <CallsListSkeleton count={5} ariaLabel="Loading calls" /> : null}
          {error && (
            <p className="text-sm text-destructive">
              {(error as Error).message}
            </p>
          )}
          {!isLoading && !error && data && data.length === 0 && (
            <div className="text-sm text-muted-foreground space-y-2">
              <p>No call records yet.</p>
              {BoardActions ? <BoardActions placement="empty" /> : null}
              <details className="rounded-md border bg-muted/20 px-3 py-2">
                <summary className="cursor-pointer text-foreground text-sm font-medium">
                  Why is this empty?
                </summary>
                <p className="mt-2 text-xs leading-relaxed">
                  Telephony webhooks must reach this app&apos;s ingest URL. If you
                  just went live, confirm Vapi (or your provider) is posting server
                  events to your production API. Operators with access can check
                  deployment settings; the exact path is{' '}
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">
                    /api/vapi/webhook/
                  </code>
                  .
                </p>
              </details>
            </div>
          )}
          {noMatchingFilter && (
            <p className="text-sm text-muted-foreground">
              No rows match this filter. Try &quot;All&quot;.
            </p>
          )}

          {!isLoading && sections && (
            <LayoutGroup>
              <div className="space-y-8">
                <section className="space-y-3">
                  <h2 className="text-sm font-semibold text-foreground">
                    In progress
                    <span className="text-muted-foreground font-normal ml-2">
                      ({sections.active.length})
                    </span>
                  </h2>
                  {sections.active.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No active calls.</p>
                  ) : (
                    <PaneResponsiveCardGrid>
                      {sections.active.map((row) => (
                        <CardMotionShell
                          key={row.id}
                          row={row}
                          completionDwell={dwellFor(row.id)}
                        />
                      ))}
                    </PaneResponsiveCardGrid>
                  )}
                </section>
                <section className="space-y-3">
                  <h2 className="text-sm font-semibold text-foreground">
                    Completed
                    <span className="text-muted-foreground font-normal ml-2">
                      ({sections.complete.length})
                    </span>
                  </h2>
                  {sections.complete.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No completed calls in this window.
                    </p>
                  ) : (
                    <PaneResponsiveCardGrid>
                      {sections.complete.map((row) => (
                        <CardMotionShell key={row.id} row={row} />
                      ))}
                    </PaneResponsiveCardGrid>
                  )}
                </section>
              </div>
            </LayoutGroup>
          )}

          {!isLoading && !sections && sortedSingleFilterRows && sortedSingleFilterRows.length > 0 && (
            <LayoutGroup>
              <PaneResponsiveCardGrid>
                {sortedSingleFilterRows.map((row) => (
                  <CardMotionShell
                    key={row.id}
                    row={row}
                    completionDwell={dwellFor(row.id)}
                  />
                ))}
              </PaneResponsiveCardGrid>
            </LayoutGroup>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
